import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { formatCurrency } from '../utils/formatCurrency';

const MONTH_LABELS = ["jan", "fév", "mar", "avr", "mai", "jun", "jul", "aoû", "sep", "oct", "nov", "déc"];

function monthKey(year, month0) {
  return `${year}-${String(month0 + 1).padStart(2, '0')}`;
}

function monthLabel(ym) {
  const [y, m] = ym.split('-');
  return `${MONTH_LABELS[parseInt(m, 10) - 1]} ${y}`;
}

function getLast13Months() {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 13; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(monthKey(d.getFullYear(), d.getMonth()));
  }
  return months; // récent → ancien
}

const ALL_MONTHS = getLast13Months();
const CURRENT_MONTH = ALL_MONTHS[0];

export default function SalarySettings({ currentUser }) {
  const isAdmin = currentUser?.role === 'admin';

  // users: [{ id, first_name }]
  const [users, setUsers] = useState([]);
  // data: { 'userId-YYYY-MM': { amount, carried_from } }
  const [data, setData] = useState({});
  // editing: { 'userId-YYYY-MM': string }  — vide si pas d'entrée propre, valeur si entrée réelle
  const [editing, setEditing] = useState({});
  const [averages, setAverages] = useState([]);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [monthsData, avgRes] = await Promise.all([
        Promise.all(ALL_MONTHS.map(m => api.get(`/household/1/income?month=${m}`))),
        api.get(`/household/1/income/average?month=${CURRENT_MONTH}&months=12`),
      ]);

      // Extraire la liste des utilisateurs depuis le premier mois
      const userList = monthsData[0].data.map(u => ({ id: u.id, first_name: u.first_name }));
      setUsers(userList);

      // Construire data
      const newData = {};
      const newEditing = {};
      ALL_MONTHS.forEach((m, i) => {
        monthsData[i].data.forEach(u => {
          const key = `${u.id}-${m}`;
          newData[key] = { amount: u.income_monthly, carried_from: u.carried_from };
          // Editing : vide si carry-forward (pas d'entrée réelle ce mois), valeur réelle sinon
          newEditing[key] = u.carried_from ? '' : (u.income_monthly != null ? String(u.income_monthly) : '');
        });
      });
      setData(newData);
      setEditing(prev => {
        // Conserver les saisies en cours (non sauvegardées) de l'utilisateur
        const merged = { ...newEditing };
        Object.keys(prev).forEach(k => {
          if (prev[k] !== '' && prev[k] !== newEditing[k]) merged[k] = prev[k];
        });
        return merged;
      });

      setAverages(avgRes.data);
    } catch (err) {
      console.error('Erreur chargement salaires:', err);
      setError('Impossible de charger les données. Vérifiez votre connexion.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (userId, month) => {
    const key = `${userId}-${month}`;
    const raw = editing[key];
    const val = parseFloat(String(raw).replace(',', '.'));
    if (!val || val <= 0) return;

    setSaving(p => ({ ...p, [key]: true }));
    try {
      await api.post(`/household/user/${userId}/income`, { month, amount: val });
      // Mettre à jour uniquement la cellule sauvegardée dans data
      const res = await api.get(`/household/1/income?month=${month}`);
      const avgRes = await api.get(`/household/1/income/average?month=${CURRENT_MONTH}&months=12`);
      setData(prev => {
        const updated = { ...prev };
        res.data.forEach(u => {
          updated[`${u.id}-${month}`] = { amount: u.income_monthly, carried_from: u.carried_from };
        });
        return updated;
      });
      setAverages(avgRes.data);
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
    } finally {
      setSaving(p => ({ ...p, [key]: false }));
    }
  };

  const handleBlur = (userId, month) => {
    save(userId, month);
  };

  const handleKeyDown = (e, userId, month) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save(userId, month);
      e.target.blur();
    }
  };

  const avgMap = {};
  averages.forEach(u => { avgMap[u.id] = u; });

  if (error) {
    return (
      <div style={{ padding: 20, color: '#b91c1c', background: '#fef2f2', borderRadius: 8 }}>
        {error}
      </div>
    );
  }

  return (
    <div>
      {/* En-tête */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 6px' }}>Historique des salaires</h3>
        <p style={{ fontSize: 14, color: 'var(--text-soft)', margin: 0 }}>
          Saisissez le salaire net réel de chaque mois. La moyenne sur 12 mois sert de référence dans le budget.
          {!isAdmin && (
            <strong style={{ marginLeft: 8, color: '#b45309' }}>
              Lecture seule — modification réservée à l'administrateur.
            </strong>
          )}
        </p>
      </div>

      {/* Cartes de moyenne */}
      {averages.length > 0 && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
          {averages.map(u => (
            <div key={u.id} style={{
              background: '#dbeafe',
              border: '1px solid #93c5fd',
              borderRadius: 10,
              padding: '14px 20px',
              minWidth: 210,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>
                Moyenne 12 mois — {u.first_name}
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#1d4ed8' }}>
                {u.average != null ? formatCurrency(u.average) : '—'}
              </div>
              {u.contribution_ratio != null && (
                <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 3 }}>
                  {(u.contribution_ratio * 100).toFixed(1)} % du foyer · sur {u.months_count} mois
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tableau */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', width: 100 }}>
                Mois
              </th>
              {users.map(u => (
                <th key={u.id} style={{ textAlign: 'center', padding: '10px 14px', fontWeight: 700, fontSize: 14, color: 'var(--text)', minWidth: 180 }}>
                  {u.first_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ALL_MONTHS.map(m => {
              const isCurrent = m === CURRENT_MONTH;
              return (
                <tr key={m} style={{ background: isCurrent ? '#eff6ff' : 'transparent', borderBottom: '1px solid var(--border)' }}>
                  <td style={{
                    padding: '9px 14px',
                    fontWeight: isCurrent ? 700 : 400,
                    color: isCurrent ? '#1d4ed8' : 'var(--text-soft)',
                    whiteSpace: 'nowrap',
                    fontSize: 13,
                  }}>
                    {monthLabel(m)}
                    {isCurrent && (
                      <span style={{ display: 'block', fontSize: 10, color: '#93c5fd', fontWeight: 600 }}>
                        ce mois
                      </span>
                    )}
                  </td>

                  {users.map(u => {
                    const key = `${u.id}-${m}`;
                    const cell = data[key];
                    const isCarried = !!cell?.carried_from;
                    const isSavingCell = !!saving[key];

                    return (
                      <td key={u.id} style={{ padding: '6px 14px', textAlign: 'center' }}>
                        {isAdmin ? (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={editing[key] ?? ''}
                              placeholder={isCarried ? formatCurrency(cell?.amount).replace(' ', ' ') : 'ex: 3500'}
                              onChange={e => setEditing(prev => ({ ...prev, [key]: e.target.value }))}
                              onBlur={() => handleBlur(u.id, m)}
                              onKeyDown={e => handleKeyDown(e, u.id, m)}
                              style={{
                                width: 120,
                                textAlign: 'right',
                                fontSize: 14,
                                fontWeight: 600,
                                padding: '5px 10px',
                                border: `1.5px solid ${isCarried ? '#cbd5e1' : '#93c5fd'}`,
                                borderRadius: 7,
                                background: isCarried ? '#f8fafc' : '#fff',
                                color: 'var(--text)',
                                outline: 'none',
                                transition: 'border-color .15s',
                              }}
                              onFocus={e => { e.target.style.borderColor = '#3b82f6'; }}
                            />
                            {isCarried && (
                              <span style={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>
                                ↩ repris de {cell.carried_from}
                              </span>
                            )}
                            {isSavingCell && (
                              <span style={{ fontSize: 10, color: '#3b82f6' }}>en cours…</span>
                            )}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <span style={{ fontWeight: 600, color: isCarried ? 'var(--text-muted)' : 'var(--text)', fontSize: 14 }}>
                              {cell?.amount != null ? formatCurrency(cell.amount) : '—'}
                            </span>
                            {isCarried && (
                              <span style={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>
                                ↩ {cell.carried_from}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            {/* Ligne récap moyenne */}
            <tr style={{ borderTop: '2px solid var(--border)', background: '#f0f9ff' }}>
              <td style={{ padding: '11px 14px', fontSize: 12, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                Moy. 12 mois
              </td>
              {users.map(u => (
                <td key={u.id} style={{ padding: '11px 14px', textAlign: 'center', fontWeight: 800, fontSize: 16, color: '#1d4ed8' }}>
                  {avgMap[u.id]?.average != null ? formatCurrency(avgMap[u.id].average) : '—'}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
